import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  collection, getDocs, query, orderBy, limit, where
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import MessageBox from "./MessageBox";
import NotificationBell from './NotificationBell'; // ✅ Added import

const Homepage = () => {
  const navigate = useNavigate();
  const [lostItemsCount, setLostItemsCount] = useState(0);
  const [foundItemsCount, setFoundItemsCount] = useState(0);
  const [recentLostItems, setRecentLostItems] = useState([]);
  const [recentFoundItems, setRecentFoundItems] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [currentUser, setCurrentUser] = useState(undefined);
  const logoutTimerRef = useRef(null);

  const showMessageBox = useCallback((msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const closeMessageBox = useCallback(() => {
    setMessage("");
    setMessageType("info");
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("registrationNumber");
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      showMessageBox("Failed to log out: " + error.message, "danger");
    }
  }, [navigate, showMessageBox]);

  const resetLogoutTimer = useCallback(() => {
    const currentTimer = logoutTimerRef.current;
    if (currentTimer) clearTimeout(currentTimer);
    logoutTimerRef.current = setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("registrationNumber");
      navigate("/login");
    }, 60 * 60 * 1000);
  }, [navigate]);

  const handleUserActivity = useCallback(() => {
    resetLogoutTimer();
  }, [resetLogoutTimer]);

  const fetchRecentItems = useCallback(async () => {
    setLoading(true);
    try {
      const lostItemsQuery = query(
        collection(db, "items"),
        where("type", "==", "lost"),
        where("status", "==", "lost"),
        orderBy("timestamp", "desc"),
        limit(6)
      );
      const lostQuerySnapshot = await getDocs(lostItemsQuery);
      const lostItemsData = lostQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentLostItems(lostItemsData);

      const allLostItemsQuery = query(
        collection(db, "items"),
        where("type", "==", "lost"),
        where("status", "==", "lost")
      );
      const allLostSnapshot = await getDocs(allLostItemsQuery);
      setLostItemsCount(allLostSnapshot.size);

      const foundItemsQuery = query(
        collection(db, "items"),
        where("type", "==", "found"),
        where("status", "==", "unclaimed"),
        orderBy("timestamp", "desc"),
        limit(6)
      );
      const foundQuerySnapshot = await getDocs(foundItemsQuery);
      const foundItemsData = foundQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentFoundItems(foundItemsData);

      const allFoundItemsQuery = query(
        collection(db, "items"),
        where("type", "==", "found"),
        where("status", "==", "unclaimed")
      );
      const allFoundSnapshot = await getDocs(allFoundItemsQuery);
      setFoundItemsCount(allFoundSnapshot.size);
    } catch (err) {
      console.log(err)
      showMessageBox("Error loading recent items. Please try again.", "danger");
    } finally {
      setLoading(false);
    }
  }, [showMessageBox]);

  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-in-out" });
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        const storedUsername = localStorage.getItem("username");
        if (storedUsername) setUsername(storedUsername);
        else if (user.displayName) setUsername(user.displayName);
        else if (user.email) setUsername(user.email);
        resetLogoutTimer();
        window.addEventListener("mousemove", handleUserActivity);
        window.addEventListener("keypress", handleUserActivity);
        window.addEventListener("scroll", handleUserActivity);
        window.addEventListener("click", handleUserActivity);
        fetchRecentItems();
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        localStorage.removeItem("registrationNumber");
        if (window.location.pathname !== "/login") {
          navigate("/login");
        }
      }
    });
    return () => {
      unsubscribeAuth();
      const currentTimer = logoutTimerRef.current;
      if (currentTimer) clearTimeout(currentTimer);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
    };
  }, [navigate, fetchRecentItems, resetLogoutTimer, handleUserActivity]);

  useEffect(() => { AOS.refresh(); }, [recentLostItems, recentFoundItems, message]);

  if (loading || currentUser === undefined) {
  return (
    <div className="min-h-screen flex flex-col bg-white animate-pulse select-none">

      {/* === Navbar Skeleton === */}
      <nav className="flex items-center justify-between h-16 px-6 border-b border-gray-300 bg-gray-100">
        <div className="bg-gray-300 w-40 h-8 rounded-md" />
        <div className="flex space-x-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-300 w-20 h-6 rounded-md" />
          ))}
          <div className="bg-gray-300 w-24 h-10 rounded-2xl" />
        </div>
      </nav>

      {/* === Hero Banner Skeleton === */}
      <section className="relative flex flex-col items-center justify-center text-center min-h-[420px] bg-gray-50 p-10 border-b border-gray-300">
        {/* Large heading */}
        <div className="bg-gray-300 w-72 h-14 rounded-lg mb-6" />
        {/* Subtext */}
        <div className="bg-gray-300 w-52 h-6 rounded-full mb-8" />
        {/* Button */}
        <div className="bg-gray-300 w-48 h-12 rounded-pill mx-auto" />
      </section>

      {/* === Main Content Skeleton === */}
      <main className="flex flex-col max-w-6xl mx-auto py-10 px-6 space-y-12">

        {/* Item Counts Section */}
        <div className="flex flex-col md:flex-row justify-center gap-8">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex-1 p-8 bg-gray-100 rounded-xl space-y-6">
              <div className="bg-gray-300 w-40 h-8 rounded-md mx-auto" />
              <div className="bg-gray-300 w-full h-6 rounded-md" />
              <div className="bg-gray-300 w-full h-6 rounded-md" />
              <div className="bg-gray-300 w-full h-6 rounded-md" />
            </div>
          ))}
        </div>

        {/* Recently Lost Items */}
        <section>
          <div className="text-center mb-7">
            <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-gray-300" />
            <div className="bg-gray-300 w-48 h-6 rounded-md mx-auto" />
            <div className="bg-gray-300 w-32 h-4 rounded-full mx-auto mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-xl p-4 flex flex-col h-full animate-pulse space-y-4"
              >
                <div className="bg-gray-300 rounded-t-xl h-40 w-full" />
                <div className="flex flex-col flex-1 space-y-3 p-3">
                  <div className="bg-gray-300 h-6 w-3/4 rounded-md" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-1/2 rounded-full" />
                  <div className="bg-gray-300 h-8 w-full rounded-lg mt-auto" />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recently Found Items */}
        <section>
          <div className="text-center mb-7">
            <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-gray-300" />
            <div className="bg-gray-300 w-48 h-6 rounded-md mx-auto" />
            <div className="bg-gray-300 w-32 h-4 rounded-full mx-auto mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-xl p-4 flex flex-col h-full animate-pulse space-y-4"
              >
                <div className="bg-gray-300 rounded-t-xl h-40 w-full" />
                <div className="flex flex-col flex-1 space-y-3 p-3">
                  <div className="bg-gray-300 h-6 w-3/4 rounded-md" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-full rounded-full" />
                  <div className="bg-gray-300 h-4 w-1/2 rounded-full" />
                  <div className="bg-gray-300 h-8 w-full rounded-lg mt-auto" />
                </div>
              </div>
            ))}
          </div>
        </section>

      </main>

      {/* === Footer Skeleton === */}
      <footer className="bg-blue-900 text-gray-200 py-10 px-6 mt-auto shadow-inner border-t-2 border-blue-900">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 text-left">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="space-y-4">
              <div className="bg-gray-300 h-6 w-32 rounded-md" />
              <ul className="space-y-2">
                {[...Array(4)].map((__, i) => (
                  <li key={i}>
                    <div className="bg-gray-300 h-4 w-full rounded-md" />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <hr className="border-gray-700 my-6" />

        <div className="text-center">
          <div className="bg-gray-300 h-4 w-40 mx-auto rounded-md" />
          <div className="bg-gray-300 h-3 w-72 mx-auto rounded-full mt-2" />
        </div>
      </footer>
    </div>
  );
}

  return (
    <>
   
      {/* Navbar */}
      <nav className=" flex items-center bg-white border-b px-6 py-3 shadow-xl sticky top-0 z-50 animate-fadeIn" data-aos="zoom-out"
      style={{
    backgroundImage: "url('/bg_4.jpg')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }}>
        <Link className="mr-4" to="/homepage" data-aos="zoom-in" data-aos-delay="150">
          <img
            src="/Christ_logo_latest.png"
            alt="Christ University Logo"
            className="logo-christ max-w-[100px] h-auto transition-transform duration-200 "
          />
        </Link>
        <div className="flex items-center ml-auto space-x-2">
          {/* ✅ Added NotificationBell component here */}
          <NotificationBell />
          <span className="font-semibold text-gray-500 mr-5 text-base" data-aos="fade-left" data-aos-delay="320">
            Welcome: <span className="text-blue-800">{username}</span>
          </span>
          <ul className="flex items-center gap-2" data-aos="fade-left" data-aos-delay="400">
           
            <li>
              <Link className="my-nav-link px-4 py-2 font-semibold border-b-2 border-transparent transition-all hover:border-blue-600 focus-visible:outline-none hover:text-blue-700 focus-visible:text-blue-700" to="/history">
                HISTORY
              </Link>
            </li>
            <li>
              <a
                className="my-nav-link px-4 py-2 font-semibold border-b-2 border-transparent transition-all hover:border-blue-600 focus-visible:outline-none hover:text-blue-700 focus-visible:text-blue-700"
                href="https://www.christuniversity.in/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ABOUT
              </a>
            </li>
            <li>
              <button
  onClick={handleLogout}
  type="button"
  className=" text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
  data-aos="fade-left"
  data-aos-delay="460"
>
  <div
    className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1024 1024"
      height="25px"
      width="25px"
    >
      <path
        d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z"
        fill="#000000"
      ></path>
      <path
        d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z"
        fill="#000000"
      ></path>
    </svg>
  </div>
  <p className="translate-x-2 wy-2">LOGOUT</p>
</button>

            </li>
          </ul>
        </div>
      </nav>
      <section
  className="relative text-white py-10 shadow-lg border-b-8 border-blue-400 overflow-hidden flex items-center justify-center text-center min-h-[420px] bg-center bg-cover animate-fadeIn"
  data-aos="fade-up"
  data-aos-delay="220"
>
  <video
    className="absolute inset-0 w-full h-full object-cover z-0"
    src="/Christ_video.mp4"
    autoPlay
    muted
    loop
    playsInline
    preload="auto"
  >
    <source src="/Christ_video.mp4" type="video/mp4" />
    Your browser does not support the video tag.
  </video>

  {/* White-ish Overlay */}
  <div className="absolute inset-0 bg-white opacity-40 z-10"></div>

  <div className=" relative z-20 flex flex-col items-center py-10 space-y-6 animate-slideInUp">
    <h2
  className="
  heading
    text-4xl
    sm:text-7xl
    md:text-8xl
    lg:text-9xl
    font-extrabold
    text-shadow
    drop-shadow-lg
    mt-4
    mb-4
    tracking-wide
    animate-fadeIn
  "
>
  Lost and Found Portal
</h2>

    <button
  onClick={() => navigate("/reportitem")}
  className="relative inline-flex items-center justify-center px-8 py-2.5 overflow-hidden tracking-tighter text-white bg-blue-800 rounded-pill group focus:outline-none focus:ring-2 focus:ring-blue-300 text-lg font-semibold shadow-sm mt-6"
  data-aos="flip-left"
  data-aos-delay="650"
>
  {/* Expanding hover ripple */}
  <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-orange-600 rounded-full group-hover:w-56 group-hover:h-56"></span>

  {/* Bottom-left SVG decoration */}
  <span className="absolute bottom-0 left-0 h-full -ml-2">
    <svg xmlns="http://www.w3.org/2000/svg" className="w-auto h-full opacity-100 object-stretch" viewBox="0 0 487 487">
      <path
        fillOpacity=".1"
        fillRule="nonzero"
        fill="#FFF"
        d="M0 .3c67 2.1 134.1 4.3 186.3 37 52.2 32.7 89.6 95.8 112.8 150.6 23.2 54.8 32.3 101.4 61.2 149.9 28.9 48.4 77.7 98.8 126.4 149.2H0V.3z"
      ></path>
    </svg>
  </span>

  {/* Top-right SVG decoration */}
  <span className="absolute top-0 right-0 w-12 h-full -mr-3">
    <svg xmlns="http://www.w3.org/2000/svg" className="object-cover w-full h-full" viewBox="0 0 487 487">
      <path
        fillOpacity=".1"
        fillRule="nonzero"
        fill="#FFF"
        d="M487 486.7c-66.1-3.6-132.3-7.3-186.3-37s-95.9-85.3-126.2-137.2c-30.4-51.8-49.3-99.9-76.5-151.4C70.9 109.6 35.6 54.8.3 0H487v486.7z"
      ></path>
    </svg>
  </span>

  {/* Subtle top-to-bottom glow */}
  <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-200"></span>

  {/* Button Text */}
  <span className="relative z-10">Report an Item Now!</span>
</button>

  </div>
</section>

      {/* Main Content */}
      
  
      <div className="container max-w-6xl mx-auto my-12 px-6 sm:px-8 animate-fadeIn">
        {message && (
          <MessageBox message={message} type={messageType} onClose={closeMessageBox} />
        )}

        {/* Item Counts */}
        <div className="flex flex-col md:flex-row justify-center gap-8 mb-12">
          <div className="flex-1">
            <div
  className="glass-card-2 p-8 "
  data-aos="fade-up"
  data-aos-delay="150"
>
              <h2 className="text-red-600 font-extrabold text-3xl mb-3 drop-shadow">Lost Items: {lostItemsCount}</h2>
              <div className="flex justify-center items-center mt-4">
  <Link
    to="/all-lost-items"
    className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-red-500 rounded-md group"
    data-aos="zoom-in"
    data-aos-delay="300"
  >
    <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-red-700 rounded group-hover:-mr-4 group-hover:-mt-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-red-700 rounded group-hover:-ml-4 group-hover:-mb-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-red-600 rounded-md group-hover:translate-x-0"></span>
    <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white ">
      View All Lost Items
    </span>
  </Link>
</div>

            </div>
          </div>
          <div className="flex-1">
           <div
  className="glass-card-2 p-8 "
  data-aos="fade-up"
  data-aos-delay="150"
>
              <h2 className="text-green-600 font-extrabold text-3xl mb-3 drop-shadow">Found Items: {foundItemsCount}</h2>
              <div className="flex justify-center items-center mt-4">
  <Link
    to="/all-found-items"
    className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-green-500 rounded-md group"
    data-aos="zoom-in"
    data-aos-delay="360"
  >
    <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-700 rounded group-hover:-mr-4 group-hover:-mt-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-700 rounded group-hover:-ml-4 group-hover:-mb-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-green-600 rounded-md group-hover:translate-x-0"></span>
    <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
      View All Found Items
    </span>
  </Link>
</div>

            </div>
          </div>
        </div>

        {/* Recently Posted Items */}
        <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
  {/* Recently Lost Items */}
  <div>
    <div className="text-center mb-7">
      <h3 className="text-red-600 font-extrabold text-2xl flex items-center justify-center gap-2 mb-2 animate-fadeIn"
        data-aos="zoom-in" data-aos-delay="100">
        <svg className="w-7 h-7 text-red-700 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12A9 9 0 103 12a9 9 0 0018 0z" />
        </svg>
        Recently Lost Items
      </h3>
      <p className="text-gray-500">Latest items reported as lost</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {recentLostItems.length > 0 ? recentLostItems.map((item, idx) => (
        <div
  key={item.id}
  className="glass-card-2 rounded-xl flex flex-col h-full border-l-4 border-red-400 transition duration-300 ease-in-out hover:border-red-600 animate-slideInUp group"
          data-aos="zoom-in-up"
          data-aos-delay={100 + idx * 95}
        >
          {item.imageUrl && (
            <img src={item.imageUrl} className="w-full h-52 object-cover rounded-t-xl" alt={item.itemName || 'Lost Item'} />
          )}
          <div className="p-5 flex flex-col flex-1">
            <h5 className="text-red-700 font-bold mb-1 text-xl">{item.itemName || 'Unknown Item'}</h5>
            <p className="text-gray-600 text-sm mb-2 line-clamp-3">{item.description || 'No description available'}</p>
            <ul className="text-xs text-gray-500 mb-3 space-y-1">
  <li><span className="font-semibold">Date:</span> {item.reportedDate || 'N/A'}</li>
</ul>

           <Link
  to={`/itemdetails/${item.id}`}
  className="my-nav-link relative inline-block p-px font-semibold leading-6 text-white shadow-2xl cursor-pointer rounded-xl transition-transform duration-300 ease-in-out hover:scale-105 active:scale-95 mt-auto w-full group"
  data-aos="flip-up"
  data-aos-delay={320 + idx * 60}
>
  <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-red-400 via-red-500 to-red-600 p-[2px] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
  <span className="relative z-10 block px-6 py-3 rounded-xl bg-red-600 group-hover:bg-red-700 transition-colors duration-300">
    <div className="relative z-10 flex items-center justify-center space-x-2">
      <span className="transition-all duration-500 group-hover:translate-x-1">
        View Details
      </span>
      <svg
        className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1"
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
        />
      </svg>
    </div>
  </span>
</Link>

          </div>
        </div>
      )) : (
        <div className="text-center py-10 w-full text-gray-400 italic animate-fadeIn">
          <svg className="mx-auto mb-2 w-9 h-9" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M3 3h18M3 7h18M3 21h18" />
          </svg>
          No recently lost items found.
        </div>
      )}
    </div>
    {recentLostItems.length > 0 && (
      <div className="text-center mt-7">
        <div className="flex justify-center items-center mt-4">
  <Link
    to="/all-lost-items"
    className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-red-600 rounded-md group"
    data-aos="zoom-in-up"
    data-aos-delay="250"
  >
    <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-red-800 rounded group-hover:-mr-4 group-hover:-mt-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-red-800 rounded group-hover:-ml-4 group-hover:-mb-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-red-700 rounded-md group-hover:translate-x-0"></span>
    <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
      View All Lost Items
    </span>
  </Link>
</div>

      </div>
      
    )}
  </div>

  {/* Recently Found Items */}
  <div>
    <div className="text-center mb-7">
      <h3 className="text-green-600 font-extrabold text-2xl flex items-center justify-center gap-2 mb-2 animate-fadeIn"
        data-aos="zoom-in" data-aos-delay="100">
        <svg className="w-7 h-7 text-green-700 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Recently Found Items
      </h3>
      <p className="text-gray-500">Latest items reported as found</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {recentFoundItems.length > 0 ? recentFoundItems.map((item, idx) => (
        <div
  key={item.id}
  className="glass-card-2 rounded-xl flex flex-col h-full border-l-4 border-green-400  hover:border-green-600 "
          data-aos="zoom-in-up"
          data-aos-delay={100 + idx * 95}
        >
          {item.imageUrl && (
            <img src={item.imageUrl} className="w-full h-52 object-cover rounded-t-xl" alt={item.itemName || 'Found Item'} />
          )}
          <div className="p-5 flex flex-col flex-1">
            <h5 className="text-green-700 font-bold mb-1 text-xl">{item.itemName || 'Unknown Item'}</h5>
            <p className="text-gray-600 text-sm mb-2 line-clamp-3">{item.description || 'No description available'}</p>
            <ul className="text-xs text-gray-500 mb-3 space-y-1">
  <li><span className="font-semibold">Date:</span> {item.reportedDate || 'N/A'}</li>
</ul>

           <Link
  to={`/itemdetails/${item.id}`}
  className="my-nav-link relative inline-block p-px font-semibold leading-6 text-white shadow-2xl cursor-pointer rounded-xl transition-transform duration-300 ease-in-out hover:scale-105 active:scale-95 mt-auto w-full group"
  data-aos="flip-up"
  data-aos-delay={320 + idx * 60}
>
  <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 via-green-500 to-green-600 p-[2px] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
  <span className="relative z-10 block px-6 py-3 rounded-xl bg-green-600 group-hover:bg-green-700 transition-colors duration-300">
    <div className="relative z-10 flex items-center justify-center space-x-2">
      <span className="transition-all duration-500 group-hover:translate-x-1">
        View Details
      </span>
      <svg
        className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1"
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
        />
      </svg>
    </div>
  </span>
</Link>

          </div>
        </div>
      )) : (
        <div className="text-center py-10 w-full text-gray-400 italic animate-fadeIn">
          <svg className="mx-auto mb-2 w-9 h-9" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M3 3h18M3 7h18M3 21h18" />
          </svg>
          No recently found items found.
        </div>
      )}
    </div>
    {recentFoundItems.length > 0 && (
      <div className="text-center mt-7">
        <div className="flex justify-center items-center mt-4">
  <Link
    to="/all-found-items"
    className="my-nav-link relative w-fit no-underline flex items-center px-5 py-2 overflow-hidden font-medium transition-all bg-green-600 rounded-md group"
    data-aos="zoom-in-up"
    data-aos-delay="250"
  >
    <span className="absolute top-0 right-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-mr-4 group-hover:-mt-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 rotate-180 left-0 inline-block w-4 h-4 transition-all duration-500 ease-in-out bg-green-800 rounded group-hover:-ml-4 group-hover:-mb-4">
      <span className="absolute top-0 right-0 w-5 h-5 rotate-45 translate-x-1/2 -translate-y-1/2 bg-white"></span>
    </span>
    <span className="absolute bottom-0 left-0 w-full h-full transition-all duration-500 ease-in-out delay-200 -translate-x-full bg-green-700 rounded-md group-hover:translate-x-0"></span>
    <span className="relative text-white transition-colors duration-200 ease-in-out group-hover:text-white">
      View All Found Items
    </span>
  </Link>
</div>

      </div>
    )}
  </div>
</div>
</div>
</div>
      {/* Footer */}

      <footer
  className="w-full bg-blue-900 text-gray-100 pt-10 pb-5 shadow-inner border-t-2 border-blue-900 animate-fadeIn"
  data-aos="fade-up"
  data-aos-delay="130"
>
   
  <div className="max-w-6xl mx-auto px-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 pb-8 text-left">
      {/* Quick Links */}
      <div data-aos="fade-right" data-aos-delay="180">
        <h6 className="font-bold mb-4 text-lg text-white">Quick Links</h6>
        <ul className="space-y-2 text-sm">
          <li>
            <Link
              to="/homepage"
              className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200 ease-in-out my-nav-link"
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/reportitem"
              className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200 ease-in-out my-nav-link"
            >
              Report Item
            </Link>
          </li>
          <li>
            <Link
              to="/history"
              className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200 ease-in-out my-nav-link"
            >
              Claims History
            </Link>
          </li>
        </ul>
      </div>

      {/* Services */}
      <div data-aos="fade-up" data-aos-delay="220">
        <h6 className="font-bold mb-4 text-lg text-white">Services</h6>
        <ul className="space-y-2 text-sm">
          <li>
            <Link
              to="/all-lost-items"
              className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200 ease-in-out my-nav-link"
            >
              Lost Items
            </Link>
          </li>
          <li>
            <Link
              to="/all-found-items"
              className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200 ease-in-out my-nav-link"
            >
              Found Items
            </Link>
          </li>
        </ul>
      </div>

      {/* Contact */}
      <div data-aos="fade-left" data-aos-delay="260">
        <h6 className="font-bold mb-4 text-lg text-white">Contact</h6>
        <ul className="space-y-2 text-sm text-gray-400">
          <li>
            <span className="inline-block w-5 mr-2">📍</span>
            Christ University, Bangalore
          </li>
          <li>
            <span className="inline-block w-5 mr-2">✉️</span>
            aadi.santhosh@bcah.christuniversity.in
          </li>
          <li>
            <span className="inline-block w-5 mr-2">✉️</span>
            allan.george@bcah.christuniversity.in
          </li>
          <li>
            <span className="inline-block w-5 mr-2">📞</span>
            +91 7356712006
          </li>
        </ul>
      </div>
    </div>

    <hr className="border-gray-600 border-opacity-30 my-6" />

    <div className="text-center">
      <small className="text-gray-400 text-xs tracking-wide">
        © 2024 Lost and Found Christ University. All rights reserved.
      </small>
    </div>
  </div>
</footer>

      
    </>
  );
};

export default Homepage;
